import type { GetMessagesQuery } from '#src/graphql/generated';

import { Alert, Box, Spinner, Text, VStack } from '@chakra-ui/react';

import { MessageCard } from '#src/components/message-card';

export type MessageListMessage = GetMessagesQuery['messages'][number];

export interface MessageListProps {
  messages: MessageListMessage[];
  loading: boolean;
  error: Error | undefined;
  emptyMessage?: string;
  parentTargetId: string | null;
  onSetParentTargetId: (id: string | null) => void;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
}

export function MessageList({
  messages,
  loading,
  error,
  emptyMessage = 'No messages found.',
  parentTargetId,
  onSetParentTargetId,
  messagesEndRef,
}: MessageListProps) {
  if (loading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text fontSize="sm" color="fg.muted" mt={4}>
          Loading messages...
        </Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert.Root status="error">
        <Alert.Indicator />
        <Alert.Title>Error loading messages: {error.message}</Alert.Title>
      </Alert.Root>
    );
  }

  if (messages.length === 0) {
    return (
      <Text color="fg.muted" textAlign="center" py={8}>
        {emptyMessage}
      </Text>
    );
  }

  return (
    <VStack gap={3} align="stretch" flex={1} overflowY="auto" minHeight={0}>
      {messages.map((message) => (
        <MessageCard
          key={message.id}
          message={message}
          childMessages={message.childMessages}
          parentTargetId={parentTargetId}
          onSetParentTargetId={onSetParentTargetId}
          showReply
        />
      ))}
      <Box ref={messagesEndRef} />
    </VStack>
  );
}
